import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { APIHOST } from '../../common';
import { useAuth, getToken } from '../auth/AuthContext';
import { Order } from '../../types/order';
import { formatDateFromYYYYMMDD } from '../../../lib/datetime';
import { useApp } from '../AppContext';
import { set } from 'date-fns';

interface SplitOrderProps {
  open: boolean;
  onClose: () => void;
  orderDocumentId: string;
  onUpdate?: () => void;
}

interface SplitOrderResponse {
  success: boolean;
  message: string;
  data?: {
    newOrder: Order;
    originalOrder: Order;
  }
}


export default function SplitOrder({ open, onClose, orderDocumentId, onUpdate }: SplitOrderProps) {
  const { currentUser } = useAuth();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedRooms, setSelectedRooms] = useState<Set<string>>(new Set());
  const [view, setView] = useState<'select' | 'review'>('select');
  const { setSnackbarInput } = useApp();
  const [response, setResponse] = useState<SplitOrderResponse | null>(null);

  useEffect(() => {
    setView('select')
    setResponse(null);
    // Load order data when dialog is mounted and has documentId
    if (open && orderDocumentId) {
      fetchOrder();
    }
  }, [open, orderDocumentId]);

  const fetchOrder = async () => {
    try {
      setLoading(true);
      setError('');

      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(
        `${APIHOST}/api/orders/${orderDocumentId}?populate[]=booker&populate[]=conference_group&populate[]=conference_hotel.hotel&populate[]=conference_hotel.conference&populate[]=order_rooms.order_room_occupants&populate[]=order_rooms.conference_hotel_room`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch order details');
      }

      const data = await response.json();
      setOrder(data.data);
      // Reset selected rooms when new order is loaded
      setSelectedRooms(new Set());

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load order');
      console.error('Split order fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOrder(null);
    setError('');
    setSelectedRooms(new Set());
    onClose();
  };

  const handleRoomSelection = (roomDocumentId: string, checked: boolean) => {
    const newSelection = new Set(selectedRooms);
    if (checked) {
      newSelection.add(roomDocumentId);
    } else {
      newSelection.delete(roomDocumentId);
    }
    setSelectedRooms(newSelection);
    if (order && order.order_rooms && newSelection.size === order?.order_rooms.length)
      setSnackbarInput({ message: 'You have selected all rooms. Splitting all rooms is not allowed.', severity: 'warning' });
  };

  const canSplit = (order: Order | null) => {
    if (!order || !order.order_rooms || order.order_rooms.length <= 1) {
      return false; // Cannot split if only 1 room or no rooms
    }
    const selectedCount = selectedRooms.size;
    return selectedCount > 0 && selectedCount < order.order_rooms.length && !['abandoned', 'draft'].includes(order.orderStatus)
  };

  const reviewSplit = async () => {
    setView('review');
  };

  const getRoomTypeColor = (type: string) => {
    switch (type) {
      case 'student': return 'primary';
      case 'chaperone': return 'secondary';
      case 'adult': return 'success';
      default: return 'default';
    }
  };

  const handleSplit = async () => { 
  
    if (!order) {
      setSnackbarInput({ message: 'No order loaded to split', severity: 'error' });
      return;
    }
    if (selectedRooms.size === 0) {
      setSnackbarInput({ message: 'No rooms selected to split', severity: 'error' });
      return;
    }
    if (!order.order_rooms) {
      setSnackbarInput({ message: 'Order has no rooms to split', severity: 'error' });
      return;
    }
    if (selectedRooms.size === order.order_rooms.length) {
      setSnackbarInput({ message: 'You cannot split all rooms from an order', severity: 'warning' });
      return;
    }

    try {
      setLoading(true);
      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${APIHOST}/api/orders/${orderDocumentId}/split`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          rooms: Array.from(selectedRooms)
        })
      });

    const res = await response.json();
   
      if (!response.ok) {
        setSnackbarInput({ message: res.error.message, severity: 'error' });
      }
      else {
         setResponse(res);
        setOrder(null);
        setSnackbarInput({ message: 'Order split successfully', severity: 'success' });
      }
      
      
    } catch (err) {
      setSnackbarInput({ message: err instanceof Error ? err.message : 'Failed to split order', severity: 'error' });
      console.error('Split order error:', err);
    } finally {
      setLoading(false);
    }
  }


  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Split Order
        {order && (
          <Typography variant="subtitle2" color="text.secondary">
            <Box sx={{float: 'left', mr: 1}}>
              Confirmation: {order.confirmation || 'No confirmation'} 
            </Box>
            {!loading && order && <Box sx={{float: 'left', mr: 3}}><Chip
                        label={order.orderStatus} 
                        color={order.orderStatus === 'received' ? 'success' : 'default'}
                        size="small"
                      /></Box>}
          </Typography>
          
        )}
      </DialogTitle>
      
      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {order && !loading && (
          <Grid container spacing={2}>

            {/* Booker Information */}
            {order.booker && order.conference_hotel && (
              <Grid size={{xs: 12}}>
                <Card>
                  <CardContent>
                    <Box display="flex" flexDirection="row" gap={1} mb={2}>
                      <Box sx={{width: '50%'}}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Conference
                        </Typography>
                        <Typography variant="body2">
                            {order.conference_hotel.conference?.name || 'N/A'}
                        </Typography>
                        <Typography variant="body2">
                            {order.conference_hotel.hotel?.longName || 'N/A'}
                        </Typography>
                      </Box>
                      <Box sx={{width: '50%'}}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Booker
                        </Typography>
                        <Typography variant="body2">
                        {order.booker.firstName} {order.booker.lastName}
                        </Typography>
                        <Typography variant="body2">
                        {order.booker.email}
                        </Typography>
                      </Box>
                    </Box>
                    
                  </CardContent>
                </Card>
              </Grid>
            )}

            {order.order_rooms && order.order_rooms.length < 2 && (
              <Grid size={{xs: 12}}>
                <Alert severity="warning" >
                  This order has less than 2 rooms and cannot be split.
                </Alert>
              </Grid>
            )}

            {['draft', 'abandoned'].includes(order.orderStatus) && (
              <Grid size={{xs: 12}}>
                <Alert severity="error" >
                  Draft and abandoned orders cannot be split. Current status is "{order.orderStatus}". 
                </Alert>
              </Grid>
            )}

             {order.order_rooms && order.order_rooms.length > 1 && ['received'].includes(order.orderStatus) && (
              <Grid size={{xs: 12}}>
                <Alert severity="info" icon={false}>
                  Select the rooms below that you want to split into a separate order. 
                </Alert>
              </Grid>
            )}


            {/* Order Rooms */}
            {view === 'select' && order.order_rooms && order.order_rooms.length > 0 && (
              <Grid size={{xs: 12}}>
                <Card>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Order Rooms ({order.order_rooms.length})
                    </Typography>
                    
                    {order.order_rooms.map((room, index) => (
                      <Box key={room.documentId || index} sx={{ mb: 2 }}>
                        <Box display="flex" flexDirection={'column'} gap={1} mb={1}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={selectedRooms.has(room.documentId || '')}
                                onChange={(e) => handleRoomSelection(room.documentId || '', e.target.checked)}
                                disabled={order.order_rooms!.length <= 1}
                                size="small"
                              />
                            }
                            label={<Box>{room.conference_hotel_room?.name} <Chip 
                                label={room.type} 
                                color={getRoomTypeColor(room.type)}
                                size="small"
                            /></Box>}
                            sx={{ mr: 1, minWidth: 'auto' }}
                          />
                          
                          <Typography variant="body2" color="text.secondary">
                            {formatDateFromYYYYMMDD(room.arrivalDate)} - {formatDateFromYYYYMMDD(room.departureDate)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ${room.dailyRate}/night
                          </Typography>
                        </Box>

                        {/* Room Occupants */}
                        {room.order_room_occupants && room.order_room_occupants.length > 0 && (
                          <Box sx={{ ml: 2 }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                              Occupants ({room.order_room_occupants.length}):
                            </Typography>
                            <List dense>
                              {room.order_room_occupants.map((occupant, occupantIndex) => (
                                <ListItem key={occupant.documentId || occupantIndex} sx={{ py: 0 }}>
                                  <ListItemText
                                    primary={`${occupant.firstName} ${occupant.lastName}`}
                                    
                                    
                                  />
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                        )}
                        
                        {index < order.order_rooms!.length - 1 && <Divider sx={{ mt: 2 }} />}
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
            )}

            {view === 'review' && (
              <Grid size={{xs: 12}}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Review Split
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Review the split below. The original order will keep the rooms on the left, and a new order will be created with the rooms on the right. After the order is split, the individual orders can be edited separately.
                    </Typography>
                    
                    <Grid container spacing={1}>
                      {/* Original Order (Unselected Rooms) */}
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Card >
                          <CardContent>
                            <Typography variant="subtitle1" gutterBottom color="primary">
                              Original Order - Keep These Rooms
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              Confirmation: {order?.confirmation || 'No confirmation'}
                            </Typography>
                            
                            {order?.order_rooms?.filter(room => !selectedRooms.has(room.documentId || '')).map((room, index) => (
                              <Box key={room.documentId || index} sx={{ mb: 2, p: 2, borderRadius: 1 }}>
                                <Box display="flex" alignItems="center" gap={1} mb={1}>
                                  <Chip 
                                    label={room.type} 
                                    color={getRoomTypeColor(room.type)}
                                    size="small"
                                  />
                                  <Typography variant="body2">
                                    {formatDateFromYYYYMMDD(room.arrivalDate)} - {formatDateFromYYYYMMDD(room.departureDate)}
                                  </Typography>
                                  <Chip 
                                    label={room.holdStatus} 
                                    color={room.holdStatus === 'held' ? 'success' : 'default'}
                                    size="small"
                                  />
                                  <Typography variant="body2" color="text.secondary">
                                    ${room.dailyRate}/night
                                  </Typography>
                                </Box>
                                
                                {/* Room Occupants */}
                                {room.order_room_occupants && room.order_room_occupants.length > 0 && (
                                  <Box sx={{ ml: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                      Occupants ({room.order_room_occupants.length}):
                                    </Typography>
                                    <List dense sx={{ py: 0 }}>
                                      {room.order_room_occupants.map((occupant, occupantIndex) => (
                                        <ListItem key={occupant.documentId || occupantIndex} sx={{ py: 0, px: 0 }}>
                                          <ListItemText
                                            primary={
                                              <Typography variant="body2">
                                                {occupant.firstName} {occupant.lastName}
                                              </Typography>
                                            }
                                          />
                                        </ListItem>
                                      ))}
                                    </List>
                                  </Box>
                                )}
                              </Box>
                            ))}
                            
                            {order?.order_rooms?.filter(room => !selectedRooms.has(room.documentId || '')).length === 0 && (
                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                No rooms will remain in the original order
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>

                      {/* New Order (Selected Rooms) */}
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Card>
                          <CardContent>
                            <Typography variant="subtitle1" gutterBottom color="primary">
                              New Order - Split These Rooms
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              New confirmation will be generated
                            </Typography>
                            
                            {order?.order_rooms?.filter(room => selectedRooms.has(room.documentId || '')).map((room, index) => (
                              <Box key={room.documentId || index} sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                                <Box display="flex" alignItems="center" gap={1} mb={1}>
                                  <Chip 
                                    label={room.type} 
                                    color={getRoomTypeColor(room.type)}
                                    size="small"
                                  />
                                  <Typography variant="body2">
                                    {formatDateFromYYYYMMDD(room.arrivalDate)} - {formatDateFromYYYYMMDD(room.departureDate)}
                                  </Typography>
                                  <Chip 
                                    label={room.holdStatus} 
                                    color={room.holdStatus === 'held' ? 'success' : 'default'}
                                    size="small"
                                  />
                                  <Typography variant="body2" color="text.secondary">
                                    ${room.dailyRate}/night
                                  </Typography>
                                </Box>
                                
                                {/* Room Occupants */}
                                {room.order_room_occupants && room.order_room_occupants.length > 0 && (
                                  <Box sx={{ ml: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                      Occupants ({room.order_room_occupants.length}):
                                    </Typography>
                                    <List dense sx={{ py: 0 }}>
                                      {room.order_room_occupants.map((occupant, occupantIndex) => (
                                        <ListItem key={occupant.documentId || occupantIndex} sx={{ py: 0, px: 0 }}>
                                          <ListItemText
                                            primary={
                                              <Typography variant="body2">
                                                {occupant.firstName} {occupant.lastName}
                                              </Typography>
                                            }
                                          />
                                        </ListItem>
                                      ))}
                                    </List>
                                  </Box>
                                )}
                              </Box>
                            ))}
                            
                            {selectedRooms.size === 0 && (
                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                No rooms selected for new order
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        )}
        {response && (
          <Box sx={{ mt: 2 }}>
            <Alert severity={response.success ? 'success' : 'error'}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Split successful! A confirmation email has been sent to {response.data?.newOrder.booker?.email}.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Note that both orders will need to re-generate their invoices after this split to reflect the updated room allocations.
                </Typography>
               
            </Alert>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, mt: 1 }}>
                <Box sx={{width: '50%'}}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Original Order
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Confirmation: {response.data?.originalOrder.confirmation}
                    </Typography>
                    <Button href={`/admin/order/${response.data?.originalOrder.documentId}`}>
                    View Original Order
                    </Button>
                </Box>
                <Box sx={{width: '50%'}}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      New Order
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Confirmation: {response.data?.newOrder.confirmation}
                    </Typography>

                    <Button href={`/admin/order/${response.data?.newOrder.documentId}`}>
                    View New Order
                    </Button>
                </Box>
                
            </Box>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose}>
          {(response && response.success) ? 'Close' : 'Cancel'}
        </Button>
        
        {view === 'select' && !response && (
          <Button
            onClick={reviewSplit}
            variant="contained"
            color="primary"
            disabled={loading || !order || !canSplit(order)}
          >
            Review Split
          </Button>
        )}
        
        {view === 'review' && !response && (
          <>
            <Button
              onClick={() => setView('select')}
              variant="outlined"
              color="primary"
            >
              Back to Selection
            </Button>
            <Button
              onClick={handleSplit}
              variant="contained"
              color="primary"
            >
              Confirm Split
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}